import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Output,
    OnInit,
    Input,
} from '@angular/core';

@Component({
    selector: 'app-custom-range-date-picker',
    templateUrl: './custom-range-date-picker.component.html',
    styleUrls: ['./custom-range-date-picker.component.scss'],
})
export class CustomRangeDatePickerComponent {

    @Output() clear = new EventEmitter<void>();
    @Output() close = new EventEmitter<void>();

    @Output() rangeSelected = new EventEmitter<{ start: Date; end: Date }>();

    onDateSelected(start: Date, end: Date) {
        this.rangeSelected.emit({ start, end });
    }
    

    popupVisible = true;
    displayValue = '';
    currentCalendarDate = new Date();

    selectedMonth: number;
    selectedYear: number;
    yearOptions: number[] = [];

    monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    calendarDays: (number | null)[][] = [];
    focusedDayIndex: { week: number; day: number } | null = null;

    // Range selection
    selectedStartDate: Date | null = null;
    selectedEndDate: Date | null = null;
    isCustomRange = false;

    activeQuickOption: string | null = null;

    constructor(private eRef: ElementRef) {
        const now = new Date();
        this.selectedMonth = now.getMonth();
        this.selectedYear = now.getFullYear();

        this.yearOptions = [];

        for (let y = 2013; y <= this.selectedYear; y++) {
            this.yearOptions.push(y);
        }

        this.generateCalendar(this.currentCalendarDate);
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.popupVisible = true;
        }
    }

    generateCalendar(date: Date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const firstWeekDay = firstDay.getDay();
        const daysInMonth = lastDay.getDate();

        this.calendarDays = [];
        let dayCounter = 1;

        for (let week = 0; week < 6; week++) {
            const weekDays: (number | null)[] = [];
            for (let day = 0; day < 7; day++) {
                const cellIndex = week * 7 + day;
                if (cellIndex >= firstWeekDay && dayCounter <= daysInMonth) {
                    weekDays.push(dayCounter++);
                } else {
                    weekDays.push(null);
                }
            }
            this.calendarDays.push(weekDays);
        }
    }

    onMonthChange() {
        this.currentCalendarDate = new Date(
            this.selectedYear,
            this.selectedMonth,
            1
        );
        this.generateCalendar(this.currentCalendarDate);
        this.focusedDayIndex = null;
    }

    onYearChange() {
        this.onMonthChange();
    }
    
    clearSelection() 
    {

        this.selectedStartDate = null;
        this.selectedEndDate = null;
        this.displayValue = '';

        this.clear.emit();   // notify parent and close the calendar 
    }

    cancelSelection(){
        this.close.emit()
    }

    isMonthDisabled(year: number, month: number): boolean {
        return false; // Allow past months
    }

    selectQuickOption(option: string) {
        const now = new Date();
        let start: Date;
        let end: Date;

        switch (option) {
            case 'yesterday':
                start = new Date(now);
                start.setDate(now.getDate() - 1);
                end = new Date(start);
                break;
            case 'today':
                start = new Date(now);
                end = new Date(now);
                break;
            case 'last30':
                start = new Date(now);
                start.setDate(now.getDate() - 30);
                end = new Date(now);
                break;
            case 'thisMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'custom':
                this.isCustomRange = true;
                this.selectedStartDate = null;
                this.selectedEndDate = null;
                this.activeQuickOption = 'custom';
                return;
        }

        this.selectedStartDate = start;
        this.selectedEndDate = end;
        this.isCustomRange = false;
        this.activeQuickOption = option;
        this.emitRange();
    }

    selectDate(day: number) {
        const year = this.currentCalendarDate.getFullYear();
        const month = this.currentCalendarDate.getMonth();
        const clickedDate = new Date(year, month, day);

        if (
            !this.selectedStartDate ||
            (this.selectedStartDate && this.selectedEndDate)
        ) {
            this.selectedStartDate = clickedDate;
            this.selectedEndDate = null;
        } else if (clickedDate < this.selectedStartDate) {
            this.selectedEndDate = this.selectedStartDate;
            this.selectedStartDate = clickedDate;
        } else {
            this.selectedEndDate = clickedDate;
        }

        this.activeQuickOption = 'custom';
        this.isCustomRange = true;

        if (this.selectedStartDate && this.selectedEndDate) {
            this.emitRange();
        }
    }

    emitRange() {
        if (!this.selectedStartDate || !this.selectedEndDate) return;

        const range = {
            start: this.selectedStartDate,
            end: this.selectedEndDate,
        };

        this.rangeSelected.emit(range);

        this.displayValue = `${this.formatDate(
            this.selectedStartDate
        )} - ${this.formatDate(this.selectedEndDate)}`;
    }

    formatDate(date: Date): string {
        const yyyy = date.getFullYear();
        const mm = (date.getMonth() + 1).toString().padStart(2, '0');
        const dd = date.getDate().toString().padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    isSelectingDate(): boolean {
        return true;
    }

    isStartDate(day: number | null): boolean {
        if (!day || !this.selectedStartDate) return false;
        return this.sameDate(
            new Date(
                this.currentCalendarDate.getFullYear(),
                this.currentCalendarDate.getMonth(),
                day
            ),
            this.selectedStartDate
        );
    }

    isEndDate(day: number | null): boolean {
        if (!day || !this.selectedEndDate) return false;
        return this.sameDate(
            new Date(
                this.currentCalendarDate.getFullYear(),
                this.currentCalendarDate.getMonth(),
                day
            ),
            this.selectedEndDate
        );
    }

    isInRange(day: number | null): boolean {
        if (!day || !this.selectedStartDate || !this.selectedEndDate)
            return false;
        const date = new Date(
            this.currentCalendarDate.getFullYear(),
            this.currentCalendarDate.getMonth(),
            day
        );
        return date > this.selectedStartDate && date < this.selectedEndDate;
    }

    sameDate(a: Date, b: Date): boolean {
        return a.toDateString() === b.toDateString();
    }
}
